import * as React from "react";
import { loader } from "graphql.macro";
import { ChatMessage } from "../Chat";
import { OperationResult, useClient } from "urql";
import {
  SendRoomChatMessageMutation,
  SendRoomChatMessageMutationVariables,
} from "./graphql/SendRoomChatMessageMutation.generated";
import { useSubscription } from "../../hooks/useSubscription";
import { useUser } from "../UserProvider";
import { RoomChatMessagesFragmentFragment } from "./graphql/RoomChatMessagesFragment.generated";
import {
  JoinRoomSubscription,
  JoinRoomSubscriptionVariables,
} from "./graphql/JoinRoomSubscription.generated";
import { useQuery } from "../../hooks/useQuery";
import {
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
} from "../Chat/graphql/GetChatMessages.generated";

const JOIN_ROOM_SUBSCRIPTION = loader("./graphql/JoinRoomSubscription.graphql");

const SEND_ROOM_CHAT_MESSAGE_MUTATION = loader(
  "./graphql/SendRoomChatMessageMutation.graphql"
);

const GET_CHAT_MESSAGES_QUERY = loader(
  "../Chat/graphql/GetChatMessages.graphql"
);

type RoomSubscriptionState = [
  Array<ChatMessage>,
  (message: string) => Promise<OperationResult<SendRoomChatMessageMutation>>
];

const RoomSubscriptionContext = React.createContext<RoomSubscriptionState>(
  undefined as any
);

const isChatMessages = (data: {
  __typename?: string;
}): data is RoomChatMessagesFragmentFragment => {
  return (
    (data as RoomChatMessagesFragmentFragment).__typename === "RoomChatMessages"
  );
};
const MAX_CHAT_MESSAGES = 100;
let localMessageId = 0;

const mergeMessages = (
  prev: Array<ChatMessage>,
  newMessage: Array<ChatMessage>
) => {
  let result = [...prev, ...newMessage];
  if (result.length > MAX_CHAT_MESSAGES)
    result = result.slice(result.length - MAX_CHAT_MESSAGES);

  return result;
};

export function SubscribeToRoom({
  id,
  children,
}: React.PropsWithChildren<{ id: string }>) {
  const [{ data: subscriptionData }] = useSubscription<
    JoinRoomSubscription,
    JoinRoomSubscription,
    JoinRoomSubscriptionVariables
  >({
    query: JOIN_ROOM_SUBSCRIPTION,
    variables: { id },
  });

  const {
    user: { id: userId, name },
  } = useUser();

  const [chatMessages, setChatMessages] = React.useState<Array<ChatMessage>>(
    []
  );
  React.useEffect(() => {
    if (subscriptionData === undefined || subscriptionData.roomData === null)
      return;

    const { roomData } = subscriptionData;

    if (isChatMessages(roomData)) {
      const messages = roomData.messages ?? [];

      setChatMessages((prev) => mergeMessages(messages, prev));
    }
  }, [subscriptionData]);

  const client = useClient();
  const chat = React.useCallback(
    (message: string) => {
      setChatMessages((prev) =>
        mergeMessages(prev, [
          {
            id: (localMessageId++).toString(),
            created: new Date().toString(),
            message: message,
            player: {
              id: userId,
              name,
            },
            isReported: false,
          },
        ])
      );
      return client
        .mutation<
          SendRoomChatMessageMutation,
          SendRoomChatMessageMutationVariables
        >(SEND_ROOM_CHAT_MESSAGE_MUTATION, {
          input: {
            roomId: id,
            message: message,
          },
        })
        .toPromise()
        .then((data) => {
          if (data.error !== undefined) throw data.error;

          return data;
        });
    },
    [client, id, userId, name]
  );

  const [{ fetching, data: roomChatMessages }] = useQuery<
    GetChatMessagesQuery,
    GetChatMessagesQueryVariables
  >({
    query: GET_CHAT_MESSAGES_QUERY,
    variables: {
      chatId: id,
      cursor: null,
      limit: 20,
    },
    requestPolicy: "cache-and-network",
  });

  React.useEffect(() => {
    if (roomChatMessages?.getChatMessages?.messages) {
      setChatMessages(roomChatMessages?.getChatMessages?.messages);
    }
  }, [roomChatMessages]);

  return (
    <RoomSubscriptionContext.Provider
      value={React.useMemo(() => [chatMessages, chat], [chat, chatMessages])}
    >
      {children}
    </RoomSubscriptionContext.Provider>
  );
}

export function useRoomChat() {
  return React.useContext(RoomSubscriptionContext);
}
